import { useCallback, useEffect, useRef, useState } from "react"
import jsQR from 'jsqr'
import { NavBack } from "../layout/Menu/NavBack";
import { useAuth } from "@/contexts/AuthContext";
import { useNotify } from "@/contexts/NotifyContext";
import axios from "axios";
import { verifyAttendance } from "@/services/EventService";
import { AttendanceVerifyReq, AttendanceVerifyRes, ResponseCode } from "iasma-datatypes"

type ScanState = 'idle' | 'starting' | 'scanning' | 'found' | 'error'

export default function ScanQR({ eventId }: { eventId: number }) {

	const { setToken, session } = useAuth();
	const { setNotify } = useNotify();

	const videoRef = useRef<HTMLVideoElement>(null)
	const canvasRef = useRef<HTMLCanvasElement>(null)
	const streamRef = useRef<MediaStream | null>(null)
	const rafRef = useRef<number | null>(null)

	const [state, setState] = useState<ScanState>('idle')
	const [qrCode, setQrCode] = useState<string | null>(null)
	const [qrVerify, setQrVerify] = useState<AttendanceVerifyRes | null>(null)

	const stopCamera = useCallback(() => {
		if (rafRef.current) cancelAnimationFrame(rafRef.current)
		rafRef.current = null
		streamRef.current?.getTracks().forEach((t) => t.stop())
		streamRef.current = null
	}, [])

	const tick = useCallback(() => {
		const video = videoRef.current
		const canvas = canvasRef.current
		if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
			rafRef.current = requestAnimationFrame(tick)
			return
		}

		canvas.width = video.videoWidth
		canvas.height = video.videoHeight
		const ctx = canvas.getContext('2d', { willReadFrequently: true })
		if (!ctx) return
		ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
		const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
		const code = jsQR(imageData.data, imageData.width, imageData.height, {
			inversionAttempts: 'dontInvert'
		})

		if (code && code.data) {
			setQrCode(code.data)
			setState('found')
			stopCamera()
			return
		}

		rafRef.current = requestAnimationFrame(tick)
	}, [stopCamera])

	useEffect(() => {
		async function verify() {
			if (qrCode != null) {
				let data: AttendanceVerifyReq = {
					OperatorId: session?.User?.Id || 0,
					EventId: eventId,
					Code: qrCode,
				};

				try {
					setNotify(null);
					setToken(axios);
					let result: AttendanceVerifyRes = await verifyAttendance(eventId, data);
					console.log(result);
					setQrVerify(result);
				} catch (error) {
					setNotify({ title: "Check QR Gagal", error, severity: 'error' });
				}

			}
		}
		verify();
	}, [qrCode])

	const startCamera = useCallback(async () => {
		setQrVerify(null)
		setNotify(null);
		setQrCode(null)
		setState('starting')
		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: { ideal: 'environment' } },
				audio: false
			})
			streamRef.current = stream
			if (videoRef.current) {
				videoRef.current.srcObject = stream
				await videoRef.current.play()
			}
			setState('scanning')
			rafRef.current = requestAnimationFrame(tick)
		} catch (err: any) {
			setState('error')
			setNotify({ title: "Could not access the camera. Check permissions and try again.", error: err, severity: 'error' });
		}
	}, [tick])

	useEffect(() => {
		return () => stopCamera()
	}, [stopCamera])

	const handleRescan = () => {
		setQrCode(null)
		startCamera()
	}

	function showQrVerify() {
		if (!qrVerify)
			return;
		let status = null;
		switch (qrVerify.Status) {
			case ResponseCode.Success:
				status = "Kode QR Otentik"; break;
			case ResponseCode.AlreadyAttend:
				status = "Sudah absensi hari ini"; break;
			case ResponseCode.CodeExpired:
				status = "Kode QR Expired, silakan buat QR baru"; break;
			case ResponseCode.EventNotMatch:
				status = "Bukan kode QR untuk acara terkait"; break;
			default:
				status = "Gagal " + qrVerify.Status; break;				
		}

		return <div>
			<div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
				<svg
					className="h-7 w-7 text-emerald-600"
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
					strokeWidth={2.5}
				>
					<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
				</svg>
			</div>
			<h1 className="mt-4 text-xl font-semibold text-slate-900 text-center">
				{status}
			</h1>
			<h1 className="mt-2 text-md font-semibold text-slate-800 text-center mb-4">
				{qrVerify.EventName}
			</h1>
			<h1 className="mt-2 text-md font-semibold text-slate-800 text-center mb-4">
				{qrVerify.AttendanceName} ({qrVerify.AttendanceYear})
			</h1>
		</div>

	}

	return (
		<div className="space-y-6">
			<NavBack />
			<div className="corner-frame relative aspect-square w-full max-w-sm mx-auto overflow-hidden rounded-xl bg-ink">
				<span className="cf-tr" />
				<span className="cf-br" />

				<video
					ref={videoRef}
					muted
					playsInline
					className={`h-full w-full object-cover ${state === 'scanning' ? 'block' : 'hidden'}`}
				/>
				<canvas ref={canvasRef} className="hidden" />

				{state === 'scanning' && (
					<div className="pointer-events-none absolute inset-x-6 top-6 bottom-6 border border-cyan/40 rounded-lg overflow-hidden">
						<div className="absolute left-0 right-0 h-0.5 bg-cyan shadow-[0_0_8px_2px_rgba(13,168,160,0.6)] animate-scan" />
					</div>
				)}

				{state !== 'scanning' && (
					<div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
						{state === 'idle' && (
							<>
								<button
									type="button"
									onClick={startCamera}
									className="rounded-lg border-2 bg-cyan px-5 py-2.5 text-sm font-medium hover:bg-cyan/90 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-paper outline-amber-600"
								>
									Start camera
								</button>
							</>
						)}
						{state === 'starting' && <p className="text-paper/70 text-sm font-mono">Requesting camera…</p>}
						{state === 'error' && (
							<>
								<button
									type="button"
									onClick={startCamera}
									className="rounded-lg bg-cyan text-white px-5 py-2.5 text-sm font-medium hover:bg-cyan/90 transition-colors"
								>
									Try again
								</button>
							</>
						)}
						{state === 'found' && qrVerify && (
							<div className="rounded-lg border border-cyan/30 bg-cyan-soft px-4 py-4 space-y-3 items-center justify-center">
								{showQrVerify()}
								<div className="flex gap-2">
									<button
										type="button"
										onClick={handleRescan}
										className="flex-1 rounded-md border border-ink/20 py-2 text-xs font-medium text-ink hover:bg-white transition-colors"
									>
										Pindai QR berikutnya
									</button>
								</div>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	)
}
